import React from 'react';
import { Target, TrendingUp } from 'lucide-react';
import ReusableBubbleChart, { BubbleChartDataPoint, BubbleChartAxis } from './ReusableBubbleChart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, ChartCard } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SEVERITY_COLORS } from './distributionUtils';

export interface RiskLandscapeDataItem {
  id: number;
  name: string;
  description?: string;
  technicaldebtscore: number;
  vulnerabilityscore: number;
  licenseissuesfound: number;
  operationalriskscore: number;
  businessriskscore: number;
  overallscore?: number;
  licensescore?: number;

}

interface RiskLandscapeChartProps {
  data: RiskLandscapeDataItem[];
  height?: number;
  className?: string;
  xAxisOptions?: ('Overall Risk Score' | 'Technical Debt' | 'Vulnerability Score' | 'License Score')[];
  yAxisOptions?: ('Overall Risk Score' | 'Technical Debt' | 'Vulnerability Score' | 'License Score')[];
  sizeOptions?: ('Operational Score')[];
  defaultXAxis?: string;
  defaultYAxis?: string;
  defaultSizeAxis?: string;
}

export const RiskLandscapeChart: React.FC<RiskLandscapeChartProps> = ({
  data,
  height = 320,
  className = '',
  xAxisOptions = ['Overall Risk Score', 'Technical Debt', 'Vulnerability Score', 'License Score'],
  yAxisOptions = ['Overall Risk Score', 'Technical Debt', 'Vulnerability Score', 'License Score'],
  sizeOptions = [],
  defaultXAxis = 'Overall Risk Score',
  defaultYAxis = 'Vulnerability Score',
  defaultSizeAxis = 'Operational Score'
}) => {
  const [xAxis, setXAxis] = React.useState(defaultXAxis);
  const [yAxis, setYAxis] = React.useState(defaultYAxis);
  const [sizeAxis, setSizeAxis] = React.useState(defaultSizeAxis);

  const transformedData: BubbleChartDataPoint[] = React.useMemo(() => {



    return data.map(app => {
      const xValue = xAxis === 'Technical Debt'
        ? app.technicaldebtscore
        : xAxis === 'Vulnerability Score'
          ? app.vulnerabilityscore
          : xAxis === 'License Score'
            ? app.licensescore
            : app.overallscore;

      const yValue = yAxis === 'Vulnerability Score'
        ? app.vulnerabilityscore
        : yAxis === 'License Score'
          ? app.licensescore
          : yAxis === 'Operational Score'
            ? app.operationalriskscore
            : yAxis === 'Technical Debt'
              ? app.technicaldebtscore
              : app.overallscore;

      return {
        name: app.name,
        x: xValue,
        y: yValue,
        size: app.operationalriskscore,
        description: app.description,
        additionalInfo: `Application ${app.id}`
      };
    });
  }, [data, xAxis, yAxis, sizeAxis]);

  const xAxisConfig: BubbleChartAxis = {
    name: xAxis,
    nameGap: 30,
    min: 0,
    max: 100
  };

  const yAxisConfig: BubbleChartAxis = {
    name: yAxis,
    nameGap: 30,
    min: 0,
    max: 100
  };

  const tooltipFormatter = (param: any) => {
    const data = param.data;

    return `
      <div class="font-medium">${data[3]}</div>
     
      <div class="font-medium mt-1">${xAxis}: ${data[0].toFixed(2)}</div>
      <div class="font-medium">${yAxis}: ${data[1].toFixed(2)}</div>
       <div>${sizeAxis}: ${data[2].toFixed(2)}</div>
    `;
  };
  
  const colorFunction = (dataPoint: BubbleChartDataPoint) => {


    return dataPoint.size >= 90 ? SEVERITY_COLORS.critical :
       dataPoint.size >= 70 ? SEVERITY_COLORS.high :
         dataPoint.size >= 40 ? SEVERITY_COLORS.medium : SEVERITY_COLORS.low;
  };


  return (
    <ChartCard
      title="Project Risk Landscape"
      description="Multi-dimensional risk analysis visualization"
      icon={Target}
      color="blue"
      children={() => (
        <ReusableBubbleChart
          data={transformedData}
          xAxis={xAxisConfig}
          yAxis={yAxisConfig}
          height={height}
          className={className}
          emptyStateIcon={Target}
          emptyStateMessage="No project data available"
         
          colorFunction={colorFunction}
          tooltipFormatter={tooltipFormatter}
          seriesName="Project Risk Landscape"
        />
      )}
      controls={() => (
        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">X-Axis:</span>
            <Select value={xAxis} onValueChange={setXAxis}>
              <SelectTrigger className="w-40 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {xAxisOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Y-Axis:</span>
            <Select value={yAxis} onValueChange={setYAxis}>
              <SelectTrigger className="w-40 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yAxisOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {sizeOptions.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Bubble Size:</span>
              <Select value={sizeAxis} onValueChange={setSizeAxis}>
                <SelectTrigger className="w-40 h-6 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sizeOptions.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

        </div>
      )}
    />
  );
};

interface ComponentHeatMapData {
  name: string;
  version?: string;
  licensescore?: number;
  vulnerabilityscore?: number;
  technicaldebtscore?: number;
  businessriskscore?: number;
  overallscore?: number;
  operationalriskscore?: number;
}

interface TechnicalDebtChartProps {
  data: ComponentHeatMapData[];
  height?: number;
  className?: string;
}

type ScoreType = 'licensescore' | 'vulnerabilityscore' | 'technicaldebtscore' | 'overallscore' | 'operationalriskscore';

const SCORE_OPTIONS = [
  { value: 'overallscore', label: 'Overall Risk Score' },
  //  { value: 'technicaldebtscore', label: 'Technical Debt Score' },
  { value: 'operationalriskscore', label: 'Operational Risk Score' },
  { value: 'vulnerabilityscore', label: 'Vulnerability Score' },

  { value: 'licensescore', label: 'License Score' },

];

export const TechnicalDebtChart: React.FC<TechnicalDebtChartProps> = ({
  data,
  height = 320,
  className = ''
}) => {
  const [selectedScore, setSelectedScore] = React.useState<ScoreType>('overallscore');

  const transformedData: BubbleChartDataPoint[] = React.useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const filtered = data.filter(component =>
      component &&
      component.name &&
      (component.technicaldebtscore !== undefined && component.technicaldebtscore !== null) &&
      (component.businessriskscore !== undefined && component.businessriskscore !== null)
    );

    return filtered.map(component => {
      const componentName = component.version ? `${component.name}@${component.version}` : component.name;
      const scoreValue = component[selectedScore] || 0;
      const sizeValue = scoreValue;

      return {
        name: componentName,
        x: component.technicaldebtscore || 0,
        y: component.businessriskscore || 0,
        size: sizeValue,
        description: ''
      };
    });
  }, [data, selectedScore]);


  const { xAxisConfig, yAxisConfig } = React.useMemo(() => {
    return {
      xAxisConfig: { name: 'Technical Debt Score', nameGap: 30, min: 0, max: 100 },
      yAxisConfig: { name: 'Business Impact Score', nameGap: 30, min: 0, max: 100 }
    };
  }, [transformedData]);

  const colorFunction = (dataPoint: BubbleChartDataPoint) => {
    const technicalDebt = dataPoint.x;
    const businessImpact = dataPoint.y;
    const riskScore = (businessImpact + technicalDebt) / 2;

    return riskScore >= 90 ? SEVERITY_COLORS.critical :
      riskScore >= 70 ? SEVERITY_COLORS.high :
        riskScore >= 40 ? SEVERITY_COLORS.medium : SEVERITY_COLORS.low;
  };

  const tooltipFormatter = (param: any) => {
    const data = param.data;
    const scoreLabel = SCORE_OPTIONS.find(opt => opt.value === selectedScore)?.label || 'Score';
    const sizeValue = (data[2]).toFixed(2);

    return `
      <div class="font-medium">${data[3]}</div>
      <div class="font-medium mt-1">Technical Debt: ${data[0].toFixed(2)}</div>
      <div class="font-medium">Business Impact: ${data[1].toFixed(2)}</div>
      <div>${scoreLabel}: ${sizeValue}</div>
    `;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Risk Analysis</CardTitle>
            <CardDescription>
              Technical debt vs overall risk with bubble size representing selected score
            </CardDescription>
          </div>
          <Select value={selectedScore} onValueChange={(value: ScoreType) => setSelectedScore(value)}>
            <SelectTrigger className="w-40 h-6 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCORE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <ReusableBubbleChart
          data={transformedData}
          xAxis={xAxisConfig}
          yAxis={yAxisConfig}
          height={height}
          className={className}
          emptyStateIcon={TrendingUp}
          emptyStateMessage="No component data available"

          colorFunction={colorFunction}
          tooltipFormatter={tooltipFormatter}
          seriesName="Component Risk Analysis"
        />
      </CardContent>
    </Card>


  );
};

export default {
  RiskLandscapeChart,
  TechnicalDebtChart
};